/**
 * Leitura da configuração do PDV.
 *
 * Antes isto vivia só dentro de Main.modal.tsx, chamado UMA vez, quando o
 * operador escolhia o PDV: a tela só mudava se ele recarregasse a página ou
 * re-selecionasse o PDV. Com a fila de configuração, o Central passa a mandar
 * alteração enquanto o PDV está aberto — então a leitura virou serviço, para
 * que a tela possa reconsultar sozinha (ver hooks/useConfigDoPdv.ts).
 *
 * A rota /pdvInfo/:uuid passou a devolver também:
 *   configVersao   — muda quando qualquer itemInfo do PDV muda; é o sinal de
 *                    "recarregue a tela"
 *   configPendente — o que a Central salvou e ainda não entrou: o que espera a
 *                    abertura do caixa, o que está a caminho agora, e o que foi
 *                    recusado por conflito
 */
import api from "./api.service";
import alfredoIcon from "../assets/alfredo-icon.svg";
import {
  PADRAO_BOTAO_REGISTRO_VAZIO,
  PADRAO_LOGO_REGISTRO_VAZIO,
} from "../dominio/padroesDaHome";

export interface ConfigPendenteDoPdv {
  aguardandoProximoPeriodo: {
    eventoUuid: string;
    itemInfoNome: string | null;
    criadoEm: string;
  }[];
  aCaminhoAgora: number;
  conflitos: { eventoUuid: string; itemInfoNome: string | null; erro: string }[];
}

export interface ConfigDoPdv {
  homeInfo: any;
  lastPeriod: any;
  selectedPeriod: any;
  saleOptions: any[];
  printers: any[];
  configVersao: string;
  configPendente: ConfigPendenteDoPdv;
}

/**
 * "O registro não trouxe nada neste campo."
 *
 * A versão anterior era `valor?.length === 0`, que devolve `undefined` (falso)
 * quando a chave não existe no registro e quando o valor é número — ou seja,
 * chave ausente atravessava como `undefined` em vez de cair no padrão. Nos dois
 * registros do dump do cliente todas as chaves existem, então isto não muda
 * nenhuma foto do gabarito; muda o caso do registro incompleto, que hoje
 * chegaria na tela sem valor nenhum.
 */
const vazio = (valor: unknown) =>
  valor === null ||
  valor === undefined ||
  (typeof valor === "string" && valor.trim() === "");

/** Busca a configuração do PDV e aplica os padrões da tela. */
export async function carregarConfigDoPdv(
  pdvUuid: string
): Promise<ConfigDoPdv> {
  const resposta = await api.get(`pdvInfo/${pdvUuid}`);
  const pdvInfo = resposta.data.pdvInfo;
  const homeInfoValue = JSON.parse(pdvInfo.homeInfo.itemInfoValue);

  return {
    homeInfo: {
      itemInfoUuid: pdvInfo.homeInfo.itemInfoUuid,
      itemInfoName: pdvInfo.homeInfo.itemInfoName,
      itemInfoType: pdvInfo.homeInfo.itemInfoType,
      itemInfoDescription: pdvInfo.homeInfo.itemInfoDescription,
      itemInfoValue: {
        ...homeInfoValue,
        logo: homeInfoValue.logo === "default" ? alfredoIcon : homeInfoValue.logo,
        logoWidth: vazio(homeInfoValue.logoWidth)
          ? PADRAO_LOGO_REGISTRO_VAZIO
          : homeInfoValue.logoWidth,
        logoHeight: vazio(homeInfoValue.logoHeight)
          ? PADRAO_LOGO_REGISTRO_VAZIO
          : homeInfoValue.logoHeight,
        backgroundColor: vazio(homeInfoValue.backgroundColor)
          ? "#BBCAFF"
          : homeInfoValue.backgroundColor,
        buttonsColor: vazio(homeInfoValue.buttonsColor)
          ? "#1D50FF"
          : homeInfoValue.buttonsColor,
        textColor: vazio(homeInfoValue.textColor)
          ? "white"
          : homeInfoValue.textColor,
        homeTitle: vazio(homeInfoValue.homeTitle)
          ? "Selecione uma opção:"
          : homeInfoValue.homeTitle,
        buttonsWidth: vazio(homeInfoValue.buttonsWidth)
          ? PADRAO_BOTAO_REGISTRO_VAZIO
          : homeInfoValue.buttonsWidth,
        buttonsHeight: vazio(homeInfoValue.buttonsHeight)
          ? PADRAO_BOTAO_REGISTRO_VAZIO
          : homeInfoValue.buttonsHeight,
        askInitialCashBalance: vazio(homeInfoValue.askInitialCashBalance)
          ? false
          : homeInfoValue.askInitialCashBalance,
        initialCashBalance: vazio(homeInfoValue.initialCashBalance)
          ? 0
          : homeInfoValue.initialCashBalance,
        printerMainIp: vazio(homeInfoValue.printerMainIp)
          ? ""
          : homeInfoValue.printerMainIp,
        printerProductionIp: vazio(homeInfoValue.printerProductionIp)
          ? ""
          : homeInfoValue.printerProductionIp,
      },
    },
    lastPeriod: pdvInfo.lastPeriod,
    selectedPeriod: pdvInfo.selectedPeriod,
    saleOptions: (pdvInfo.saleOptions || []).map((saleOption: any) => ({
      ...saleOption,
      itemInfoValue: JSON.parse(saleOption.itemInfoValue),
    })),
    printers: pdvInfo.printers || [],
    configVersao: pdvInfo.configVersao || "",
    configPendente: pdvInfo.configPendente || {
      aguardandoProximoPeriodo: [],
      aCaminhoAgora: 0,
      conflitos: [],
    },
  };
}
